import * as yup from "yup";

import type {
	CreateAdvanceQuotaData,
	CreateInvoiceBackedQuotaData,
	CreateQuotaConsumerData,
	SyncQuotaConsumersData,
	UpdateQuotaDetailsData,
} from "~/modules/quota/api/quota/quotaTypes.ts";
import type {
	CreateAdvanceQuotaFormData,
} from "~/modules/quota/components/CreateQuotaSidebar/components/CreateAdvanceQuotaForm.tsx";
import type {
	CreateInvoiceBackedQuotaFormData,
} from "~/modules/quota/components/CreateQuotaSidebar/components/CreateInvoiceBackedQuotaForm.tsx";
import type { QuotaConsumerOrderInfo } from "~/modules/quota/components/formSections/QuotaConsumersFormSection.tsx";
import type {
	UpdateQuotaConsumersFormData,
} from "~/modules/quota/components/QuotaDetailsView/components/UpdateQuotaConsumersSidebar/UpdateQuotaConsumersForm.tsx";
import type {
	UpdateQuotaDetailsFormData,
} from "~/modules/quota/components/QuotaDetailsView/components/UpdateQuotaDetailsSidebar/UpdateQuotaDetailsForm.tsx";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";

const AMOUNT_MIN_MESSAGE = "Das Kontingent muss größer als 0 € sein.";
const CONSUMER_BUDGETS_EXCEED_AMOUNT_MESSAGE = "Die Summe der zugewiesenen Budgets darf das Kontingent nicht überschreiten.";

// The core fields that every quota form has. Spread them into the form's yup object.
export function getQuotaCoreDetailsSchemaFields() {
	return {
		title: yup.string().required(),
		managedBy: yup.string().required(),
		clientContactPerson: yup.string().required(),
		comment: yup.string().nullable().default(null),
		usableFrom: yup.date().nullable().default(null),
		// Optional. When both dates are set, the end date must be after the start date.
		usableUntil: yup.date().nullable().default(null)
			.when("usableFrom", {
				is: (value: Date | null) => Boolean(value),
				then: (fieldSchema) => fieldSchema
					.min(yup.ref("usableFrom"), "Das Startdatum muss vor dem Enddatum liegen."),
			}),
	};
}

// One assigned consumer. The rules are the same for both quota types and for the consumer edit.
// A consumer that tops up the order budget must have a budget value.
// The input coerces an empty field to 0, so reject 0 as well as null.
function getConsumerBudgetCentsAssignedSchema() {
	return yup.number().nullable().default(null)
		.when("topsUpBudget", {
			is: true,
			then: (fieldSchema) => fieldSchema
				.required("Bitte weise ein Budget zu, wenn die Bestellung erhöht wird.")
				.min(1, "Bitte weise ein Budget zu, wenn die Bestellung erhöht wird."),
		});
}

function getQuotaConsumerSchema() {
	return yup.object({
		orderId: yup.string().required(),
		topsUpBudget: yup.boolean().required().default(false),
		budgetCentsAssigned: getConsumerBudgetCentsAssignedSchema(),
		timeTrackingEnabled: yup.boolean().default(false),
		comment: yup.string().nullable().default(null),
	});
}

// The assigned consumers of a create form.
export function getQuotaConsumersSchema() {
	return yup.array().of(getQuotaConsumerSchema()).required();
}

type ConsumersBudgetTestValue = {
	amountCents?: number | null;
	consumers?: { budgetCentsAssigned?: number | null; budgetCentsConsumed?: number | null }[] | null;
};

// The sum of all assigned consumer budgets must not exceed the quota amount.
// Both forms hold the amount in `amountCents`, so the error goes to that path.
type RequiredBudgetConsumer = {
	budgetCentsAssigned?: number | null;
	budgetCentsConsumed?: number | null;
};

// The budget that the consumers hold. Each consumer takes its assigned budget, or its consumed budget
// when it draws from the shared pot. This is the backend pot rule (ConsumerSetWithinPots) and the
// detach rule. A 0 budget is an empty input. The create forms have no consumed budget, so there the
// sum is the assigned total.
export function getQuotaRequiredCents(consumers: RequiredBudgetConsumer[]): number {
	return consumers.reduce(
		(sum, consumer) => sum + (consumer.budgetCentsAssigned || (consumer.budgetCentsConsumed ?? 0)),
		0,
	);
}

export const consumersBudgetWithinAmountTest: yup.TestConfig<ConsumersBudgetTestValue> = {
	name: "consumersBudgetWithinAmount",
	message: CONSUMER_BUDGETS_EXCEED_AMOUNT_MESSAGE,
	test: (value, context) => {
		const consumers = value.consumers ?? [];
		const amountCents = value.amountCents ?? 0;
		const committedTotal = getQuotaRequiredCents(consumers);
		if (committedTotal > amountCents) {
			const hasConsumedBudget = consumers.some((consumer) => (consumer.budgetCentsConsumed ?? 0) > 0);
			return context.createError({
				path: "amountCents",
				message: hasConsumedBudget
					? `Das Kontingent reicht nicht aus. Zugewiesen und verfügt: ${formatCentsToCurrency(committedTotal)} von ${formatCentsToCurrency(amountCents)}.`
					: CONSUMER_BUDGETS_EXCEED_AMOUNT_MESSAGE,
			});
		}
		return true;
	},
};

// The distinct project ids of the given consumers' orders.
export function getConsumerProjectIds(
	consumers: CreateQuotaConsumerData[],
	orderInfoById: Record<string, QuotaConsumerOrderInfo>,
): string[] {
	return Array.from(new Set(
		consumers
			.map((consumer) => orderInfoById[consumer.orderId]?.projectId)
			.filter((id): id is string => Boolean(id)),
	));
}

export function getCreateAdvanceQuotaSchema(orderInfoById: Record<string, QuotaConsumerOrderInfo>) {
	return yup.object({
		...getQuotaCoreDetailsSchemaFields(),
		type: yup.string().oneOf(["advance"]).required().default("advance"),
		amountCents: yup.number().required().min(1, AMOUNT_MIN_MESSAGE),
		clientContactPersonId: yup.string().nullable().default(null),
		invoiceRecipientId: yup.string().nullable().default(null),
		invoiceProjectId: yup.string().nullable().default(null),
		invoiceDeliveryStartDate: yup.date().required(),
		invoiceDeliveryEndDate: yup
			.date()
			.required()
			.min(yup.ref("invoiceDeliveryStartDate"), "Das Startdatum muss vor dem Enddatum liegen.")
			.default(null),
		orderNumber: yup.string().required(),
		paymentTargetDays: yup.number().required(),
		paymentTargetDaysCustom: yup.number()
			.nullable()
			.when("paymentTargetDays", (field1Value: number[], schema) => {
				return field1Value[0] === 0
					? schema.required()
					: schema.nullable();
			}),
		consumers: getQuotaConsumersSchema(),
	}).test("atLeastOneField", "Bitte wähle einen Kontakt oder zentralen Rechnungsempfänger aus.", (value) =>
		Boolean(value.clientContactPersonId || value.invoiceRecipientId),
	).test(
		consumersBudgetWithinAmountTest,
	).test(
		"invoiceProjectRequired",
		// The consumers can belong to several projects. When they do, the invoice
		// needs one target project. The field is required only in that case.
		"Bitte wähle ein Projekt für die Rechnung aus.",
		(value, context) => {
			const projectIds = getConsumerProjectIds(value.consumers ?? [], orderInfoById);
			if (projectIds.length > 1 && !value.invoiceProjectId) {
				return context.createError({
					path: "invoiceProjectId",
					message: "Bitte wähle ein Projekt für die Rechnung aus.",
				});
			}
			return true;
		},
	);
}

export function getCreateInvoiceBackedQuotaSchema() {
	return yup.object({
		...getQuotaCoreDetailsSchemaFields(),
		type: yup.string().oneOf(["invoice-backed"]).required().default("invoice-backed"),
		invoiceIds: yup.array().of(yup.string().required()).required(),
		// With invoices, the form computes the amount from their excess. Without invoices
		// (a bare shell), the user types the amount, so it must be greater than 0.
		amountCents: yup.number().required()
			.when("invoiceIds", {
				is: (invoiceIds: string[] | undefined) => !invoiceIds?.length,
				then: (fieldSchema) => fieldSchema.min(1, AMOUNT_MIN_MESSAGE),
			}),
		consumers: getQuotaConsumersSchema(),
	}).test(
		consumersBudgetWithinAmountTest,
	);
}

// Any consumer can have an assigned budget (its own slice), with or without a top-up (BE4).
// A 0 budget is an empty input, so send null for it. A consumer with null draws from the shared pot.
function prepareConsumerBudgetCentsAssigned(budgetCentsAssigned: number | null): number | null {
	return budgetCentsAssigned !== 0 ? budgetCentsAssigned : null;
}

// Prepare the consumers array for the create payload.
export function prepareCreateQuotaConsumers(
	consumers: CreateQuotaConsumerData[],
): CreateQuotaConsumerData[] {
	return consumers.map((consumer) => ({
		...consumer,
		budgetCentsAssigned: prepareConsumerBudgetCentsAssigned(consumer.budgetCentsAssigned),
	}));
}

// Build the advance quota create payload from the form data.
export function prepareCreateAdvanceQuotaPayload(
	formData: CreateAdvanceQuotaFormData,
): CreateAdvanceQuotaData {
	return {
		...formData,
		invoiceDeliveryStartDate: formatDateToYYYYMMDD(formData.invoiceDeliveryStartDate),
		invoiceDeliveryEndDate: formatDateToYYYYMMDD(formData.invoiceDeliveryEndDate),
		usableFrom: formData.usableFrom ? formatDateToYYYYMMDD(formData.usableFrom) : null,
		usableUntil: formData.usableUntil ? formatDateToYYYYMMDD(formData.usableUntil) : null,
		paymentTargetDays: formData.paymentTargetDays === 0
			? formData.paymentTargetDaysCustom || 0
			: formData.paymentTargetDays,
		consumers: prepareCreateQuotaConsumers(formData.consumers),
	};
}

// Build the invoice-backed quota create payload from the form data. With invoices, send the
// invoice ids and the consumers, but no amount: the backend computes it from the excess.
// Without invoices (a bare shell), send the typed amount only. The backend rejects consumers
// without invoices.
export function prepareCreateInvoiceBackedQuotaPayload(
	formData: CreateInvoiceBackedQuotaFormData,
): CreateInvoiceBackedQuotaData {
	const commonData = {
		type: "invoice-backed" as const,
		title: formData.title,
		managedBy: formData.managedBy,
		clientContactPerson: formData.clientContactPerson,
		comment: formData.comment,
		usableFrom: formData.usableFrom ? formatDateToYYYYMMDD(formData.usableFrom) : null,
		usableUntil: formData.usableUntil ? formatDateToYYYYMMDD(formData.usableUntil) : null,
	};

	if (formData.invoiceIds.length === 0) {
		return {
			...commonData,
			amountCents: formData.amountCents,
		};
	}

	return {
		...commonData,
		invoiceIds: formData.invoiceIds,
		consumers: prepareCreateQuotaConsumers(formData.consumers),
	};
}

// Build the details edit payload from the form data. The backend takes all fields on each request.
// An empty text input sends null.
export function prepareUpdateQuotaDetailsPayload(
	formData: UpdateQuotaDetailsFormData,
): UpdateQuotaDetailsData {
	return {
		title: formData.title,
		managedBy: formData.managedBy || null,
		clientContactPerson: formData.clientContactPerson || null,
		comment: formData.comment || null,
		usableFrom: formData.usableFrom ? formatDateToYYYYMMDD(formData.usableFrom) : null,
		usableUntil: formData.usableUntil ? formatDateToYYYYMMDD(formData.usableUntil) : null,
		preselectForTimeTracking: formData.preselectForTimeTracking,
	};
}

// The consumer edit form. It holds the quota amount only for the shared budget check.
// A stored consumer keeps its id in `consumerId`, because the field array owns the `id` key.
export function getUpdateQuotaConsumersSchema() {
	return yup.object({
		amountCents: yup.number().required(),
		consumers: yup.array().of(getQuotaConsumerSchema().shape({
			consumerId: yup.string().nullable().default(null),
			budgetCentsConsumed: yup.number().required().default(0),
			// An assigned budget must not be below the consumed budget of the consumer, with or without a top-up.
			budgetCentsAssigned: getConsumerBudgetCentsAssignedSchema().test(
				"budgetNotBelowConsumed",
				(value, context) => {
					const { budgetCentsConsumed } = context.parent;
					if (value === null || value === undefined || value >= budgetCentsConsumed) {
						return true;
					}
					return context.createError({
						message: `Das zugewiesene Budget darf nicht unter dem verfügten Budget (${formatCentsToCurrency(budgetCentsConsumed)}) liegen.`,
					});
				},
			),
		})).required(),
	}).test(
		consumersBudgetWithinAmountTest,
	);
}

// Build the consumer sync payload from the form data. It holds the full target set.
// As on create, every consumer sends its assigned budget, or null when the field is empty.
export function prepareSyncQuotaConsumersPayload(
	formData: UpdateQuotaConsumersFormData,
): SyncQuotaConsumersData {
	return {
		consumers: formData.consumers.map((consumer) => ({
			id: consumer.consumerId,
			orderId: consumer.orderId,
			topsUpBudget: consumer.topsUpBudget,
			budgetCentsAssigned: prepareConsumerBudgetCentsAssigned(consumer.budgetCentsAssigned),
			timeTrackingEnabled: consumer.timeTrackingEnabled ?? false,
		})),
	};
}
